import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  ScrollArea,
  SimplePagination,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@devhop/ui";
import { History, UserMinus, UserPlus } from "lucide-react";
import type { UserWithAssignmentDTO } from "@/modules/assign-position/domain/contracts";
import { formatCurrentSalaryGradeLine } from "@/modules/assign-position/presentation/utils/format-current-salary-grade-line";
import { config } from "@/shared/lib/config";
import { getInitials } from "@/shared/lib/utils";
import { DraggableEmployee } from "./DraggableEmployee";

type EmployeeListProps = {
  data: UserWithAssignmentDTO[];
  isLoading: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  onAssign: (user: UserWithAssignmentDTO) => void;
  /** ປິດຕໍາແໜ່ງປະຈຸບັນ (ມີແຕ່ເມື່ອມີຕໍາແໜ່ງ) */
  onClearPosition?: (user: UserWithAssignmentDTO) => void | Promise<void>;
  clearingUserId?: string | null;
  onViewHistory?: (user: UserWithAssignmentDTO) => void;
  canManage: boolean;
};

export function EmployeeList({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  onAssign,
  onClearPosition,
  clearingUserId,
  onViewHistory,
  canManage,
}: EmployeeListProps) {
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(totalCount / limit);

  const handlePageChange = (page: number) => {
    onPaginationChange((page - 1) * limit, limit);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {(() => {
          const skeletons = [];
          for (let i = 0; i < 5; i++) {
            skeletons.push(
              <div
                key={`loading-skeleton-${i}`}
                className="flex items-center space-x-4 rounded-lg border p-4"
              >
                <div className="h-12 w-12 animate-pulse rounded-full bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
                </div>
                <div className="h-8 w-24 animate-pulse rounded bg-muted" />
              </div>,
            );
          }
          return skeletons;
        })()}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">ບໍ່ມີພະນັກງານ</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-20rem)] space-y-4">
      <div className="">
        {data.map((employee) => {
          const salaryGradeLine = formatCurrentSalaryGradeLine(employee);
          return (
          <div
            key={employee.id}
            className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-muted/50"
          >
            <div className="flex items-center space-x-4">
              <Avatar className="h-12 w-12">
                <AvatarImage
                  src={
                    employee.image ? config.apiUrl + employee.image : undefined
                  }
                  alt={employee.name ?? "avatar"}
                />
                <AvatarFallback>
                  {getInitials(employee.name ?? employee.email)}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1">
                <DraggableEmployee employee={employee} canManage={canManage} />

                {employee.currentPositionTitle && (
                  <div className="mt-1 flex flex-col">
                    <span className="text-muted-foreground text-sm">
                      {employee.currentPositionTitle}
                    </span>
                    {employee.currentDepartmentName && (
                      <span className="text-muted-foreground text-xs">
                        {employee.currentDepartmentName}
                      </span>
                    )}
                    {salaryGradeLine ? (
                      <span className="text-muted-foreground text-xs">
                        ລະດັບ: {salaryGradeLine}
                      </span>
                    ) : null}
                  </div>
                )}

                {!employee.currentPositionTitle && (
                  <span className="text-muted-foreground text-sm">ບໍ່ມີຕໍາແໜ່ງ</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1">
              {canManage && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onAssign(employee)}
                      className="h-8 w-8 p-0"
                    >
                      <UserPlus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {employee.currentPositionId ? "ປ່ຽນຕໍາແໜ່ງ" : "ກໍາຫນົດຕໍາແໜ່ງ"}
                  </TooltipContent>
                </Tooltip>
              )}
              {canManage && employee.currentPositionId && onClearPosition && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void onClearPosition(employee)}
                      disabled={clearingUserId === employee.id}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    >
                      <UserMinus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>ເອົາຕໍາແໜ່ງອອກ</TooltipContent>
                </Tooltip>
              )}
              {onViewHistory && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewHistory(employee)}
                      className="h-8 w-8 p-0"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>ປະຫວັດການກໍາຫນົດຕໍາແໜ່ງ</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center pt-4">
          <SimplePagination
            page={currentPage}
            onPageChange={handlePageChange}
            totalPages={totalPages}
            className="mx-0 w-auto"
          />
        </div>
      )}
    </ScrollArea>
  );
}
